import { WebTrackUser, BisTrackSettings, BasketHeader, BasketLineSuper, ProductBasketLine } from 'ep-bt-field-agent-proxy';

export class EpbtFaShoppingCartModel {
    m_user: WebTrackUser;
    m_settings: BisTrackSettings;
    m_header: BasketHeader;
    m_contents: BasketLineSuper[] = [];
    m_userMaximumOrderValue: number;
    //  m_promotionCodeContainer:PromotionCodeContainer;
    m_cartMergeExceededMaximumAllowedValue: boolean;

    // General Cart Properties

    /// <summary>
    /// User associated with this cart.
    /// </summary>

    private _userId: number;
    public get userId(): number {
        return this.m_user.userId;
    }

    /// <summary>
    /// Header details for the current cart.
    /// </summary>

    private _header: BasketHeader;
    public get header(): BasketHeader {
        return this.m_header;
    }

    /// <summary>
    /// Contents of the cart.
    /// </summary>

    private _contents: BasketLineSuper[];
    public get contents(): BasketLineSuper[] {
        return this._contents;
    }
    public set contents(value: BasketLineSuper[]) {
        this._contents = value;
    }

    /// <summary>
    /// Subset of cart lines that are product lines. Useful for internal actions/calculations on product-only lines.
    ///
    /// NOTE: Don't expose this to outside -- keep as a private helper property.
    /// NOTE: This probably won't be useful anymore if we add further "product" BasketLine subclasses.
    /// NOTE: Switched to protected to be able to use in WebServiceCart initialization
    /// </summary>

    private _productContents: ProductBasketLine[];
    public get productContents(): ProductBasketLine[] {
        // return this.m_contents;
        return null;
    }

    // protected List<ProductBasketLine> ProductContents
    // {
    //     get
    //     {
    //         return m_contents.FindAll(
    //             delegate (BasketLine line)
    //             {
    //                 return line is ProductBasketLine;
    //             })
    //             .ConvertAll<ProductBasketLine>(
    //             delegate (BasketLine line)
    //             {
    //                 return line as ProductBasketLine;
    //             });
    //     }
    // }

    /// <summary>
    /// Generate a copy of the contents to which lines can be added without modifying the cart.
    /// </summary>
    /// <returns></returns>
    CopyContents(): BasketLineSuper[] {
        const lines: BasketLineSuper[] = [];
        this.contents.forEach(line => {
            lines.push(line);
        });
        // this.Contents.ForEach(
        //     delegate (BasketLine line)
        //     {
        //         lines.Add(line.Clone());
        //     });

        return lines;
    }

    /// <summary>
    /// The number of items (lines) in this cart.
    /// </summary>
    private _count: number;
    public get count(): number {
        return this.m_contents.length;
    }

    /// <summary>
    /// The number of product lines that do not display a price, but show "Call for Quote" instead.
    /// </summary>
    private _callForQuoteLinesCount: number;
    public get callForQuoteLinesCount(): number {
        let count = 0;
        this.productContents.forEach(line => {
            if (line.product.showCallForQuote) {
                count++;
            }
        });
        return count;
    }

    /// <summary>
    /// The currently logged in user's maximum allowed order value
    /// </summary>
    private _userMaximumOrderValue: number;
    public get userMaximumOrderValue(): number {
        return (this.m_userMaximumOrderValue <= 0 ? -1 : this.m_userMaximumOrderValue);
    }
    public set userMaximumOrderValue(value: number) {
        this.m_userMaximumOrderValue = (value <= 0 ? -1 : value);
    }

    /// <summary>
    /// Determines whether cart totals should include VAT or not
    /// </summary>
    private _useVatInclusivePricing: boolean;
    public get useVatInclusivePricing(): boolean {
        if (this.m_user.userId == -1) {
            // WebTrackUser.InvalidUserId
            return this.m_settings.useVatInclusivePricingByDefault;
        } else {
            return this.m_user.customer.useVatInclusivePricing;
        }
    }

    // Cart Totals

    /// <summary>
    /// The total value of the items in the cart
    /// </summary>
    private _cartTotal: number;
    public get cartTotal(): number {
        // return new ProductSummationBasketLineVisitor(
        //     delegate (ProductBasketLine line)
        //     {
        //         return line.TotalSellPrice;
        //     })
        //     .CalculateTotal(this.Contents);
        return -1;
    }

    /// <summary>
    /// If a product in the cart is set as "Call for Quote"
    /// </summary>
    private _cartTotalCallForQuote: boolean;
    public get cartTotalCallForQuote(): boolean {
        // return new ProductSummationBasketLineVisitor(
        //     delegate (ProductBasketLine line)
        //     {
        //         return line.TotalSellPrice;
        //     })
        //     .CalculateTotal(this.Contents);
        return this.callForQuoteLinesCount > 0;
    }

    /// <summary>
    /// The total tax value of the items in the cart
    /// </summary>

    private _cartTotalTax: number;
    public get cartTotalTax(): number {
        //         return new ProductSummationBasketLineVisitor(
        //             delegate (ProductBasketLine line)
        //             {
        //                 return line.TotalTax;
        //             })
        //             .CalculateTotal(this.Contents);
        return -1;
    }

    /// <summary>
    /// The total value of goods in the shopping cart including tax.
    /// </summary>

    private _cartTotalIncludingTax: number;
    public get cartTotalIncludingTax(): number {
        let total: number = this.cartTotal;

        if (this.m_user.customer.useVatInclusivePricing || (this.useVatInclusivePricing && this.m_user.userId == -1)) {
            total += this.cartTotalTaxForNonVATInclusiveProducts;
        } else {
            total += this.cartTotalTax;
        }
        return total;
    }

    /// <summary>
    /// Total tax, if dealing with a separate VAT component.
    /// </summary>

    private _separateVATComponent: number;
    public get separateVATComponent(): number {
        // If using VAT inclusive pricing, the total for each line already contains the VAT component, hence the total will
        // also include VAT so we should return 0.
        // When *not* using VAT inclusive pricing, return the total amount of tax for the cart (calculated by the ShoppingCart)

        if (!this.m_settings.newTaxMode) {
            if (this.m_user.customer.useVatInclusivePricing ||
                (this.m_settings.useVatInclusivePricingByDefault && this.m_user.userId == -1)) {
                return 0;
            } else {
                return this.cartTotalTax;
            }
        }

        return 0;
    }

    /// <summary>
    /// The total tax value of the items in the cart that are not VAT Inclusive
    /// </summary>
    private _cartTotalTaxForNonVATInclusiveProducts: number;
    public get cartTotalTaxForNonVATInclusiveProducts(): number {
        //     return new ProductSummationBasketLineVisitor(
        //         delegate(ProductBasketLine line)
        //                 {
        //             if(!line.VATInclusive)
        //     {
        //         return line.TotalSellPrice;
        //     }
        //                     else
        //     {
        //         return 0M;
        //     }
        // })
        //                 .CalculateTotal(this.Contents);
        return -1;
    }

    // Cart Total Dimensions (Length/Width/Weight) for calculation of delivery charge


    /// <summary>
    /// The total weight of the items in the cart.
    /// <remarks>bisTrack Delivery Charge weights are stored as numeric(18, 4).</remarks>
    /// </summary>
    private _totalWeight: number;
    public get totalWeight(): number {
        // return new ProductSummationBasketLineVisitor(
        //     delegate (ProductBasketLine line)
        //     {
        //         return line.Product.Weight * line.Quantity;
        //     })
        //     .CalculateTotal(this.Contents);
        return -1;
    }


    /// <summary>
    /// Whether all lines in the cart have a weight, or not.
    /// </summary>

    private _allProductsHaveWeight: boolean;
    public get allProductsHaveWeight(): boolean {
        // return new ProductConditionBooleanANDVisitor(
        //     delegate (ProductBasketLine line)
        //     {
        //         return line.Product.Weight > 0;
        //     })
        //     .CalculateTotal(this.Contents);
        return false;
    }


    /// <summary>
    /// Whether any lines have products flagged as DeliveryNotAllowedOutsideLocalZone.
    /// </summary>

    private _deliveryNotAllowedOutsideLocalZone: boolean;
    public get deliveryNotAllowedOutsideLocalZone(): boolean {
        // return !(new ProductConditionBooleanANDVisitor(
        //     delegate (ProductBasketLine line)
        //     {
        //         return !line.Product.DeliveryNotAllowedOutsideLocalZone;
        //     })
        //     .CalculateTotal(this.Contents));
        return false;
    }

    private _maximumLength: number;
    public get maximumLength(): number {
        // if (this.ProductContents.Count == 0)
        //         {
        //             return 0;
        //         }

        //         if (this.ProductContents.Count == 1)
        //         {
        //             return (int)Math.Round(this.ProductContents[0].Product.Length);
        //         }

        //         // Sort the list to find the greatest length
        //         List<ProductBasketLine> localBasket = new List<ProductBasketLine>(this.ProductContents);
        //         localBasket.Sort(delegate (ProductBasketLine currentItem, ProductBasketLine nextItem)
        //         {
        //             return Product.CompareLength(currentItem.Product, nextItem.Product);
        //         });

        //         // The last item in the list contains the product with the greatest length
        //         return (int)Math.Round(localBasket[localBasket.Count - 1].Product.Length, 0, MidpointRounding.AwayFromZero);
        return -1;
    }

    private _maximumWidth: number;
    public get maximumWidth(): number {
        // if (this.ProductContents.Count == 0)
        //         {
        //             return 0;
        //         }

        //         if (this.ProductContents.Count == 1)
        //         {
        //             return (int)Math.Round(this.ProductContents[0].Product.Width);
        //         }

        //         // Sort the list to find the greatest width
        //         List<ProductBasketLine> localBasket = new List<ProductBasketLine>(this.ProductContents);
        //         localBasket.Sort(delegate (ProductBasketLine currentItem, ProductBasketLine nextItem)
        //         {
        //             return Product.CompareWidth(currentItem.Product, nextItem.Product);
        //         });

        //         // The last item in the list contains the product with the greatest width
        //         return (int)Math.Round(localBasket[localBasket.Count - 1].Product.Width, 0, MidpointRounding.AwayFromZero);
        return -1;
    }

    // Cart Operations (Add/Delete/Update)

    /// <summary>
    /// Clear all contents of the cart.
    /// </summary>
    clear() {
        // var haveData = this.m_contents.length > 0 || this.m_promotionCodeContainer.PromotionCodeDetails.Count > 0;

        // m_contents.Clear();
        // m_promotionCodeContainer.PromotionCodeDetails.Clear();
        // this.Save();

        // if (haveData)
        // {
        //     this.FireDataChangedEvent();
        // }
    }


    /// <summary>
    /// Default Constructor
    /// </summary>
    constructor(user: WebTrackUser, settings: BisTrackSettings) {
        if (user == null) {
            // throw new ArgumentNullException("user");
        }

        if (settings == null) {
            // throw new ArgumentNullException("settings");
        }

        this.m_header = new BasketHeader();
        this.m_user = user;
        this.m_settings = settings;
        this.m_userMaximumOrderValue = user.maximumOrderValue;

        // m_promotionCodeContainer = new PromotionCodeContainer(m_header.BasketId);
        // m_promotionCodeContainer.PromotionCodeAdded += new PromotionCodeContainer.PromotionCodeSuccessfullyAddedHandler(PromotionCodeAdded);
        // m_promotionCodeContainer.PromotionCodeDeleted += new PromotionCodeContainer.PromotionCodeSuccessfullyDeletedHandler(PromotionCodeDeleted);
    }
}
